import { useState } from "react";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { useReveal } from "@/hooks/useReveal";
import { RESULTS } from "@/lib/clinic";

export default function Gallery() {
  const [active, setActive] = useState(0);
  const [ref, visible] = useReveal();

  const current = RESULTS[active];
  const go = (dir) =>
    setActive((i) => (i + dir + RESULTS.length) % RESULTS.length);

  return (
    <section
      id="gallery"
      data-testid="section-gallery"
      className="relative py-24 md:py-32 bg-[#1C221F] text-white overflow-hidden"
    >
      {/* subtle grain */}
      <div aria-hidden className="absolute inset-0 opacity-[0.06] mix-blend-overlay pointer-events-none" style={{
        backgroundImage:
          "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
      }} />
      <div aria-hidden className="absolute -top-20 right-0 w-[400px] h-[400px] rounded-full bg-[#D4B27E]/10 blur-3xl" />

      <div ref={ref} className={`container-x relative reveal ${visible ? "is-visible" : ""}`}>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
          <div className="max-w-xl">
            <span className="inline-block text-xs uppercase tracking-[0.25em] text-[#D4B27E] font-medium">
              Real results
            </span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05]">
              A decade of transformations,{" "}
              <span className="text-[#D4B27E]">documented.</span>
            </h2>
            <p className="mt-4 text-white/60 max-w-md text-sm sm:text-base">
              Actual clinical photos from City Smiles Dental Clinic — the work speaks for itself.
            </p>
          </div>

          <div className="hidden sm:flex items-center gap-2">
            <button
              onClick={() => go(-1)}
              data-testid="btn-gallery-prev"
              aria-label="Previous"
              className="w-11 h-11 rounded-full border border-white/15 hover:bg-white/10 flex items-center justify-center transition-colors"
            >
              <ArrowLeft className="w-4 h-4" strokeWidth={1.75} />
            </button>
            <button
              onClick={() => go(1)}
              data-testid="btn-gallery-next"
              aria-label="Next"
              className="w-11 h-11 rounded-full border border-white/15 hover:bg-white/10 flex items-center justify-center transition-colors"
            >
              <ArrowRight className="w-4 h-4" strokeWidth={1.75} />
            </button>
          </div>
        </div>

        <div className="mt-10 md:mt-14 grid lg:grid-cols-12 gap-8 items-center">
          {/* Big image */}
          <div className="lg:col-span-7">
            <div className="relative aspect-[4/3] w-full rounded-3xl overflow-hidden bg-white/5 border border-white/10">
              <img
                key={current.key}
                src={current.image}
                alt={current.label}
                className="w-full h-full object-contain md:object-cover fade-up"
              />
              <div className="absolute top-4 left-4 bg-[#D4B27E] text-[#1C221F] text-xs font-semibold uppercase tracking-widest rounded-full px-3 py-1.5">
                {current.label}
              </div>
              <div className="absolute bottom-4 right-4 bg-black/50 backdrop-blur-md text-white/90 text-xs rounded-full px-3 py-1.5 border border-white/10">
                {String(active + 1).padStart(2, "0")} / {String(RESULTS.length).padStart(2, "0")}
              </div>
            </div>
            <p className="mt-4 text-sm text-white/70 max-w-md">{current.caption}</p>
          </div>

          {/* Vertical thumbnail list */}
          <div className="lg:col-span-5">
            <ul className="grid grid-cols-3 sm:grid-cols-6 lg:grid-cols-2 gap-3">
              {RESULTS.map((r, i) => (
                <li key={r.key}>
                  <button
                    onClick={() => setActive(i)}
                    data-testid={`gallery-thumb-${r.key}`}
                    className={`group w-full text-left rounded-2xl overflow-hidden border transition-all ${
                      i === active
                        ? "border-[#D4B27E] ring-1 ring-[#D4B27E]/50"
                        : "border-white/10 hover:border-white/30"
                    }`}
                  >
                    <div className="aspect-[4/3] bg-white/5 overflow-hidden">
                      <img
                        src={r.image}
                        alt={r.label}
                        loading="lazy"
                        className={`w-full h-full object-cover transition-transform duration-500 ${
                          i === active ? "scale-105" : "group-hover:scale-105"
                        }`}
                      />
                    </div>
                    <div className="p-2.5">
                      <div className={`text-[11px] font-medium ${i === active ? "text-[#D4B27E]" : "text-white/70"}`}>
                        {r.label}
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>

            <div className="mt-6 flex sm:hidden items-center gap-2">
              <button onClick={() => go(-1)} aria-label="Previous" className="w-11 h-11 rounded-full border border-white/15 flex items-center justify-center">
                <ArrowLeft className="w-4 h-4" strokeWidth={1.75} />
              </button>
              <button onClick={() => go(1)} aria-label="Next" className="w-11 h-11 rounded-full border border-white/15 flex items-center justify-center">
                <ArrowRight className="w-4 h-4" strokeWidth={1.75} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
